import { useState, useEffect } from 'react';
import client from '../../api/client';
import betClient from '../../api/betClient';

const SPORTS = [
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' },
  { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' },
  { key: 'boxing_boxing', label: 'Boxing' },
];

const MARKETS = [
  { key: 'h2h', label: 'Moneyline' },
  { key: 'spreads', label: 'Spread' },
  { key: 'totals', label: 'Over/Under' },
];

const BOOKMAKERS = [
  'draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'bovada', 'other',
];

const inputCls = "w-full px-3 py-2 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#0f1219] text-[14px] text-[#111827] dark:text-[#e2e8f0] placeholder-[#9CA3AF] focus:outline-none focus:border-[#005BB3] dark:focus:border-[#3b82f6] transition-colors duration-150";
const labelCls = "text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8] block mb-1";

export default function AddBetForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState({
    sport_key: 'americanfootball_nfl',
    home_team: '',
    away_team: '',
    market: 'h2h',
    selection: '',
    odds: '',
    point: '',
    stake: '',
    bookmaker: 'draftkings',
    commence_time: '',
    event_id: '',
    notes: '',
  });
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch games when sport changes
  useEffect(() => {
    setLoadingGames(true);
    setGames([]);
    // Reset game selection when sport changes
    setForm((f) => ({ ...f, home_team: '', away_team: '', commence_time: '', event_id: '' }));
    client.get(`/api/odds/${form.sport_key}`)
      .then((r) => setGames(Array.isArray(r.data) ? r.data : []))
      .catch(() => setGames([]))
      .finally(() => setLoadingGames(false));
  }, [form.sport_key]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function selectGame(eventId) {
    const game = games.find((g) => g.id === eventId);
    if (!game) return;
    setForm((f) => ({
      ...f,
      home_team: game.home_team,
      away_team: game.away_team,
      commence_time: game.commence_time || '',
      event_id: game.id,
    }));
  }

  // Auto-fill odds + point from live game data when all fields are set
  useEffect(() => {
    if (manualEntry || !form.event_id || !form.market || !form.selection || !form.bookmaker) return;
    const game = games.find((g) => g.id === form.event_id);
    if (!game) return;

    // Find matching bookmaker (try exact, then fallback to first available)
    const bm = game.bookmakers?.find((b) => b.key === form.bookmaker)
      || game.bookmakers?.[0];
    if (!bm) return;

    const mkt = bm.markets?.find((m) => m.key === form.market);
    if (!mkt) return;

    const outcome = mkt.outcomes?.find((o) => o.name === form.selection);
    if (!outcome) return;

    setForm((f) => ({
      ...f,
      odds: String(outcome.price),
      point: outcome.point != null ? String(outcome.point) : f.point,
    }));
  }, [form.event_id, form.market, form.selection, form.bookmaker, games, manualEntry]);

  function submit(e) {
    e.preventDefault();
    if (!form.home_team || !form.away_team || !form.selection || !form.odds || !form.stake) {
      setError('Fill in all required fields.');
      return;
    }
    setSubmitting(true); setError(null);

    const payload = {
      ...form,
      odds: parseFloat(form.odds),
      stake: parseFloat(form.stake),
      point: form.point ? parseFloat(form.point) : null,
      commence_time: form.commence_time || null,
      event_id: form.event_id || null,
      notes: form.notes || null,
    };

    betClient.post('/api/bets', payload)
      .then(() => onSuccess())
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSubmitting(false));
  }

  const needsPoint = form.market === 'spreads' || form.market === 'totals';

  return (
    <form onSubmit={submit} className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
          <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Add Bet</h3>
        </div>
        <button type="button" onClick={() => setManualEntry(!manualEntry)}
          className="text-[14px] text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
          {manualEntry ? 'Pick from games' : 'Manual entry'}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
        {/* Sport */}
        <div>
          <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Sport</label>
          <select value={form.sport_key} onChange={(e) => update('sport_key', e.target.value)} className={inputCls}>
            {SPORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {/* Game selector or manual team inputs */}
        {!manualEntry ? (
          <div className="col-span-2 sm:col-span-2 lg:col-span-3">
            <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Game *</label>
            {loadingGames ? (
              <div className="h-[30px] rounded bg-[#f1f5f9] dark:bg-[#0f1219] skeleton" />
            ) : games.length === 0 ? (
              <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] py-1" style={{ fontFamily: 'var(--font-sans)' }}>No upcoming games.
                <button type="button" onClick={() => setManualEntry(true)} className="text-[#005BB3] dark:text-[#3b82f6] ml-1" style={{ fontFamily: 'var(--font-sans)' }}>Enter manually</button>
              </p>
            ) : (
              <select value={form.event_id} onChange={(e) => selectGame(e.target.value)} className={inputCls}>
                <option value="">Select a game...</option>
                {games.map((g) => {
                  const t = new Date(g.commence_time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                  return <option key={g.id} value={g.id}>{g.away_team} @ {g.home_team} — {t}</option>;
                })}
              </select>
            )}
            {form.home_team && !manualEntry && (
              <p className="text-[10px] text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-mono)' }}>
                {form.away_team} @ {form.home_team}
              </p>
            )}
          </div>
        ) : (
          <>
            <div>
              <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Away Team *</label>
              <input type="text" value={form.away_team} onChange={(e) => update('away_team', e.target.value)} placeholder="e.g. Buffalo Bills" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Home Team *</label>
              <input type="text" value={form.home_team} onChange={(e) => update('home_team', e.target.value)} placeholder="e.g. Kansas City Chiefs" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Game Date</label>
              <input type="date" value={form.commence_time ? form.commence_time.slice(0, 10) : ''} onChange={(e) => update('commence_time', e.target.value)} className={inputCls} />
            </div>
          </>
        )}

        {/* Market */}
        <div>
          <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Market</label>
          <select value={form.market} onChange={(e) => { update('market', e.target.value); update('selection', ''); }} className={inputCls}>
            {MARKETS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>

        {/* Pick */}
        <div>
          <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Pick *</label>
          {form.home_team && form.away_team ? (
            <select value={form.selection} onChange={(e) => update('selection', e.target.value)} className={inputCls}>
              <option value="">Select pick...</option>
              {form.market === 'totals' ? (
                <>
                  <option value="Over">Over</option>
                  <option value="Under">Under</option>
                </>
              ) : (
                <>
                  <option value={form.home_team}>{form.home_team}</option>
                  <option value={form.away_team}>{form.away_team}</option>
                </>
              )}
            </select>
          ) : (
            <input type="text" value={form.selection} onChange={(e) => update('selection', e.target.value)}
              placeholder={form.market === 'totals' ? 'Over or Under' : 'Team name'}
              className={inputCls} />
          )}
        </div>

        {/* Odds */}
        <div>
          <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Odds (American) *</label>
          <input type="number" value={form.odds} onChange={(e) => update('odds', e.target.value)} placeholder="-110" className={inputCls} />
        </div>

        {/* Point (spread/total) */}
        {needsPoint && (
          <div>
            <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>{form.market === 'spreads' ? 'Spread' : 'Total'}</label>
            <input type="number" step="0.5" value={form.point} onChange={(e) => update('point', e.target.value)} placeholder={form.market === 'spreads' ? '-3.5' : '47.5'} className={inputCls} />
          </div>
        )}

        {/* Stake */}
        <div>
          <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Stake ($) *</label>
          <input type="number" min="0" step="1" value={form.stake} onChange={(e) => update('stake', e.target.value)} placeholder="100" className={inputCls} />
        </div>

        {/* Bookmaker */}
        <div>
          <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Bookmaker</label>
          <select value={form.bookmaker} onChange={(e) => update('bookmaker', e.target.value)} className={inputCls}>
            {BOOKMAKERS.map((bk) => <option key={bk} value={bk}>{bk.charAt(0).toUpperCase() + bk.slice(1)}</option>)}
          </select>
        </div>
      </div>

      {/* Notes */}
      <div className="mb-3">
        <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Notes</label>
        <input type="text" value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Optional notes..." className={inputCls} />
      </div>

      {error && <p className="text-[#DC2626] text-[13px] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>{error}</p>}

      <div className="flex gap-2">
        <button type="submit" disabled={submitting}
          className="px-4 py-2 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
          {submitting ? 'Saving...' : 'Save Bet'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] text-[14px] text-[#6B7280] dark:text-[#8892a8] hover:border-[#005BB3] hover:text-[#005BB3] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
          Cancel
        </button>
      </div>
    </form>
  );
}
